import { useEffect, useState } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { AppLoader } from '@/components/AppLoader'
import { useLLMConnectionStore } from '@/stores/llmConnectionStore'
import { ensureThesisPlatformConnection } from '@/lib/thesisPlatform'
import { useSubscriptionStore } from '@/stores/subscriptionStore'

import DefaultLayout from '@/pages/layout'
import NovelsIndex from '@/pages/novels/index'
import CreateNovel from '@/pages/novels/CreateNovel'

import NovelLayout from '@/pages/NovelWorkspace/layout'
import NovelManuscript from '@/pages/NovelManuscript/index'

import NovelSettings from '@/pages/novels/settings'
import AuthModal from '@/pages/Auth/AuthModal'
import PricingModal from '@/pages/Pricing/PricingModal'
import HelpModal from '@/pages/Help/HelpModal'
import { PAPERS_PATH, paperPath } from '@/constants/routes'

/** /novels/:id/... → /papers/:id/... */
function NovelsLegacyRedirect() {
  const { novelId } = useParams()
  const location = useLocation()
  const rest = location.pathname.replace(/^\/novels\/[^/]+/, '') || '/manuscript'
  return <Navigate to={`${paperPath(novelId!, rest)}${location.search}`} replace />
}

function App() {
  const [isInitialized, setIsInitialized] = useState(false)
  const [initError, setInitError] = useState<string | null>(null)
  // const { initializeData } = useNovelIndexDBStore()
  const { refreshAllModels } = useLLMConnectionStore()

  useEffect(() => {
    const checkInitialization = async () => {
      try {
        // 等待IndexDB初始化完成
        // const initPromise = initService.getInitPromise()
        // if (initPromise) {
        //   await initPromise
        // }

        // 初始化store数据
        // await initializeData()
        ensureThesisPlatformConnection()
        await useSubscriptionStore.getState().refreshMe()
        ensureThesisPlatformConnection()
        await refreshAllModels()
        ensureThesisPlatformConnection()

        setIsInitialized(true)
      } catch (error) {
        console.error('应用初始化失败:', error)
        setInitError(error instanceof Error ? error.message : '初始化失败')
      }
    }

    checkInitialization()
  }, [])

  // 如果还在初始化中，显示加载界面
  if (!isInitialized) {
    return <AppLoader message="论文台加载中..." />
  }

  // 如果初始化失败，显示错误信息
  if (initError) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontFamily: 'Arial, sans-serif',
        color: '#ff4d4f',
        textAlign: 'center',
        padding: '20px',
      }}>
        <div>
          <h2>应用初始化失败</h2>
          <p>请刷新页面重试，或检查浏览器是否支持IndexDB</p>
          <p style={{ fontSize: '12px', color: '#999' }}>错误信息: {initError}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '16px',
              padding: '8px 16px',
              backgroundColor: '#1890ff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            刷新页面
          </button>
        </div>
      </div>
    )
  }

  return (
    <Router>
      <Routes>
        <Route element={<DefaultLayout />}>
          <Route index element={<Navigate to={PAPERS_PATH} replace />} />
          <Route path={PAPERS_PATH} element={<NovelsIndex />} />
          <Route path={`${PAPERS_PATH}/create`} element={<CreateNovel />} />
          {/* 旧书签兼容 */}
          <Route path="/novels" element={<Navigate to={PAPERS_PATH} replace />} />
          <Route path="/novels/create" element={<Navigate to={`${PAPERS_PATH}/create`} replace />} />
          {/* 不对外暴露提示词库 */}
          <Route path="/prompts" element={<Navigate to={PAPERS_PATH} replace />} />
          <Route path="/prompts/:promptId" element={<Navigate to={PAPERS_PATH} replace />} />
        </Route>
        <Route path={`${PAPERS_PATH}/:novelId`} element={<NovelLayout />}>
          <Route index element={<Navigate to="manuscript" replace />} />
          {/* 旧「目录」整页 → 正文（目录已嵌在稿面左侧） */}
          <Route path="storyboard" element={<Navigate to="manuscript" replace />} />
          <Route path="prompts" element={<Navigate to="manuscript" replace />} />
          <Route path="prompts/:promptId" element={<Navigate to="manuscript" replace />} />
          <Route path="manuscript" element={<NovelManuscript />} />
          <Route path="review" element={<Navigate to="manuscript" replace />} />
          <Route path="chat-thread" element={<Navigate to="manuscript" replace />} />
          <Route path="settings">
            <Route index element={<Navigate to="basic" replace />} />
            <Route path=":tabId" element={<NovelSettings />} />
          </Route>
        </Route>
        <Route
          path="/novels/:novelId/*"
          element={<NovelsLegacyRedirect />}
        />
        <Route path="manuscript" element={<NovelManuscript />} />
      </Routes>
      <AuthModal />
      <PricingModal />
      <HelpModal />
    </Router>
  )
}

export default App 